import { useState, useCallback } from 'react';
import { categoryService, CategoryTreeItem } from '../services/categoryService';
import { APP_CONFIG } from '../constants';

export interface UseCategoriesInfiniteOptions {
  /** Исключить категорию по id (например текущую при редактировании) */
  excludeId?: number;
  /** Размер страницы при подгрузке (по умолчанию из APP_CONFIG) */
  pageSize?: number;
}

export function useCategoriesInfinite(options: UseCategoriesInfiniteOptions = {}) {
  const { excludeId, pageSize = APP_CONFIG.DEFAULT_PAGE_SIZE } = options;
  const [categories, setCategories] = useState<CategoryTreeItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    const nextPage = page;
    setLoading(true);
    setError(null);
    try {
      const response = await categoryService.getCategoriesList(nextPage, pageSize);
      const items = response.data.filter((cat) => cat.id !== excludeId);
      setCategories((prev) => (nextPage === 1 ? items : [...prev, ...items]));
      setHasMore(response.current_page < response.last_page);
      setPage(response.current_page + 1);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load categories'));
    } finally {
      setLoading(false);
    }
  }, [page, hasMore, loading, excludeId, pageSize]);

  const reset = useCallback(() => {
    setCategories([]);
    setPage(1);
    setHasMore(true);
  }, []);

  return { categories, loadMore, hasMore, loading, error, reset };
}
